import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Leaf, Search, Flame, Dumbbell, Plus, Loader2, HeartPulse } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, inr, apiError } from "@/lib/api";

const SORTS = [
  { key: "health_score", label: "Health Score" },
  { key: "protein", label: "Protein ↑" },
  { key: "calories", label: "Calories ↓" },
  { key: "price", label: "Price ↓" },
];

export default function HealthyMode() {
  const nav = useNavigate();
  const { addItem, count } = useCart();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("health_score");
  const [filters, setFilters] = useState({ veg: false, high_protein: false, low_cal: false });
  const [adding, setAdding] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    const p = new URLSearchParams({ sort });
    if (q.trim()) p.set("q", q.trim());
    if (filters.veg) p.set("veg", "true");
    if (filters.high_protein) p.set("high_protein", "true");
    if (filters.low_cal) p.set("low_cal", "true");
    try { const { data } = await api.get(`/healthy?${p}`); setItems(data.items); }
    catch { setItems([]); }
  }, [q, sort, filters]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const toggle = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));

  const add = async (it) => {
    setAdding(it.id);
    try {
      const res = await addItem({ id: it.restaurant_id }, { menu_item_id: it.menu_item_id, qty: 1 });
      if (res?.ok) toast.success(`${it.name} added to cart`);
    } catch (e) { toast.error(apiError(e)); } finally { setAdding(""); }
  };

  const scoreColor = (s) => (s >= 75 ? "bg-emerald-100 text-emerald-700" : s >= 55 ? "bg-lime-100 text-lime-700" : "bg-amber-100 text-amber-700");

  return (
    <div className="min-h-screen bg-surface max-w-[900px] mx-auto pb-24" data-testid="healthy-mode">
      <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3 flex items-center gap-2">
        <button onClick={() => nav(-1)} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
        <h1 className="font-display font-bold text-lg flex items-center gap-2"><HeartPulse size={20} className="text-emerald-600" /> Healthy Mode</h1>
        {count > 0 && <button onClick={() => nav("/cart")} className="ml-auto bg-coral text-white text-sm font-bold px-3 py-1.5 rounded-full" data-testid="healthy-cart-btn">Cart · {count}</button>}
      </div>

      <div className="px-4 py-4">
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl p-4">
          <p className="font-display font-bold text-lg">Eat smart, feel great</p>
          <p className="text-sm text-white/85 mt-0.5">Dishes ranked by real health scores &amp; nutrition. No GST — Enjoy bro.</p>
        </div>

        <div className="mt-3 flex gap-2">
          <div className="flex-1 flex items-center gap-2 bg-white border border-border rounded-xl px-3">
            <Search size={16} className="text-ink-600" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search healthy dishes" className="flex-1 py-3 text-sm outline-none bg-transparent" data-testid="healthy-search" />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-border rounded-xl px-3 text-sm" data-testid="healthy-sort">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <FilterChip active={filters.veg} onClick={() => toggle("veg")} icon={Leaf} label="Veg" testid="filter-veg" />
          <FilterChip active={filters.high_protein} onClick={() => toggle("high_protein")} icon={Dumbbell} label="High Protein" testid="filter-protein" />
          <FilterChip active={filters.low_cal} onClick={() => toggle("low_cal")} icon={Flame} label="Low Calorie" testid="filter-lowcal" />
        </div>

        {items === null ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center" data-testid="healthy-empty">
            <HeartPulse size={40} className="mx-auto text-ink-600 opacity-40" />
            <p className="mt-3 font-semibold text-ink">No healthy dishes match your filters</p>
            <p className="text-sm text-ink-600">Try clearing a filter or searching a different dish.</p>
          </div>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="healthy-grid">
            {items.map((it) => (
              <div key={it.id} className="bg-white rounded-2xl border border-border overflow-hidden flex" data-testid={`healthy-item-${it.id}`}>
                <img src={it.image} alt={it.name} className="w-24 h-full min-h-[120px] object-cover" />
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-3 h-3 rounded-sm border ${it.veg ? "border-veg" : "border-red-500"} flex items-center justify-center`}><span className={`w-1.5 h-1.5 rounded-full ${it.veg ? "bg-veg" : "bg-red-500"}`} /></span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${scoreColor(it.healthy_score)}`}>Health {it.healthy_score}</span>
                  </div>
                  <div className="font-bold text-sm text-ink mt-1 truncate">{it.name}</div>
                  <div className="text-xs text-ink-600 truncate">{it.restaurant_name}</div>
                  <div className="flex gap-3 mt-1.5 text-[11px] text-ink-600">
                    <span className="flex items-center gap-1"><Flame size={11} className="text-orange-500" /> {it.nutrition?.calories ?? "—"} kcal</span>
                    <span className="flex items-center gap-1"><Dumbbell size={11} className="text-blue-500" /> {it.nutrition?.protein ?? "—"} g protein</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-ink">{inr(it.eff_price)}</span>
                    <button onClick={() => add(it)} disabled={adding === it.id} className="bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 tap" data-testid={`healthy-add-${it.id}`}>
                      {adding === it.id ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border tap ${active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-ink border-border"}`}>
      <Icon size={14} /> {label}
    </button>
  );
}
